import type { CSSProperties } from 'react';
import type { GlobalToken } from 'antd/es/theme/interface';

export type ControlTowerZone =
  | 'attention'
  | 'neutral'
  | 'actions'
  | 'metricsCollections'
  | 'metricsPayables'
  | 'metricsDrafts'
  | 'metricsDocPrep'
  | 'metricsOrders'
  | 'metricsFulfilment'
  | 'documentPrep'
  | 'docPrepCustomerAdvance'
  | 'docPrepSupplierAdvance'
  | 'docPrepSalesInvoice'
  | 'docPrepSupplierBill'
  | 'dueToday'
  | 'pendingApprovals'
  | 'pendingReview'
  | 'overdue'
  | 'dueSoon'
  | 'monthEnd'
  | 'collectionsDetail'
  | 'ordersAtRisk'
  | 'ordersInProgress'
  | 'pendingWorkOrder'
  | 'deliveries'
  | 'deliveryNotInvoiced'
  | 'deliveryPartial'
  | 'deliveryInTransit'
  | 'deliveryDocPackage'
  | 'crmFollowUps'
  | 'crmStale'
  | 'crmActivities'
  | 'crmQuotations'
  | 'crmMissingPo'
  | 'quotationConversion'
  | 'quotationAwaitingOrder'
  | 'quotationPrAwaitingSo'
  | 'quotationConversionAll'
  | 'teamPipeline'
  | 'myTasks'
  | 'scmApprovalPr'
  | 'scmApprovalBid'
  | 'scmApprovalPo'
  | 'scmApprovalGrn'
  | 'scmApprovalReturn'
  | 'sourcingQueue'
  | 'qmsGateIir'
  | 'qmsGateStage'
  | 'qmsGateFir'
  | 'qmsApprovalIrn'
  | 'qmsApprovalFirReport'
  | 'qmsApprovalPlan'
  | 'qmsNcrQueue'
  | 'qmsCapaOverdue'
  | 'qmsPlanItemsQueue';

export type ZoneStyle = {
  borderColor: string;
  cardStyle?: CSSProperties;
  headerStyle?: CSSProperties;
  bodyStyle?: CSSProperties;
  stripStyle?: CSSProperties;
};

const stripStyle = (token: GlobalToken, bg: string, borderColor: string): ZoneStyle['stripStyle'] => ({
  background: bg,
  borderRadius: token.borderRadiusLG,
  padding: 16,
  border: `1px solid ${token.colorBorderSecondary}`,
});

const subPanelStyle = (token: GlobalToken, bg: string, borderColor: string): CSSProperties => ({
  background: bg,
  borderRadius: token.borderRadius,
  padding: 12,
  borderLeft: `3px solid ${borderColor}`,
});

export function getZoneStyle(token: GlobalToken, zone: ControlTowerZone): ZoneStyle {
  const baseCard: CSSProperties = {
    borderLeft: `4px solid`,
    borderRadius: token.borderRadiusLG,
  };

  switch (zone) {
    case 'attention':
      return {
        borderColor: token.colorWarning,
        stripStyle: stripStyle(token, token.colorWarningBg, token.colorWarning),
        cardStyle: { ...baseCard, borderLeftColor: token.colorWarning },
        headerStyle: { background: token.colorWarningBg, borderBottom: `1px solid ${token.colorBorderSecondary}` },
        bodyStyle: { background: token.colorWarningBg },
      };
    case 'neutral':
      return {
        borderColor: token.colorTextSecondary,
        stripStyle: stripStyle(token, token.colorFillAlter, token.colorTextSecondary),
        cardStyle: { ...baseCard, borderLeftColor: token.colorTextSecondary },
        headerStyle: { background: token.colorFillAlter, borderBottom: `1px solid ${token.colorBorderSecondary}` },
        bodyStyle: { background: token.colorFillAlter },
      };
    case 'actions':
    case 'myTasks':
      return {
        borderColor: token.colorPrimary,
        cardStyle: { ...baseCard, borderLeftColor: token.colorPrimary },
        headerStyle: { background: token.colorPrimaryBg, borderBottom: `1px solid ${token.colorBorderSecondary}` },
        bodyStyle: { background: token.colorPrimaryBg },
      };
    case 'metricsCollections':
      return { borderColor: token.colorSuccess, stripStyle: stripStyle(token, token.colorSuccessBg, token.colorSuccess) };
    case 'metricsPayables':
      return { borderColor: token.colorWarning, stripStyle: stripStyle(token, token.colorWarningBg, token.colorWarning) };
    case 'metricsDrafts':
    case 'teamPipeline':
      return {
        borderColor: token.colorTextSecondary,
        stripStyle: stripStyle(token, token.colorFillAlter, token.colorTextSecondary),
        cardStyle: { ...baseCard, borderLeftColor: token.colorTextSecondary },
        headerStyle: { background: token.colorFillAlter, borderBottom: `1px solid ${token.colorBorderSecondary}` },
        bodyStyle: { background: token.colorFillAlter },
      };
    case 'metricsDocPrep':
    case 'metricsFulfilment':
      return { borderColor: token.colorInfo, stripStyle: stripStyle(token, token.colorInfoBg, token.colorInfo) };
    case 'metricsOrders':
      return { borderColor: token.colorPrimary, stripStyle: stripStyle(token, token.colorPrimaryBg, token.colorPrimary) };
    case 'documentPrep':
    case 'deliveries':
    case 'crmFollowUps':
    case 'quotationConversion':
      return {
        borderColor: token.colorInfo,
        cardStyle: { ...baseCard, borderLeftColor: token.colorInfo },
        headerStyle: { background: token.colorInfoBg, borderBottom: `1px solid ${token.colorBorderSecondary}` },
        bodyStyle: { background: token.colorBgContainer },
      };
    case 'docPrepCustomerAdvance':
    case 'deliveryNotInvoiced':
    case 'crmStale':
    case 'quotationAwaitingOrder':
    case 'scmApprovalPr':
    case 'qmsGateIir':
      return {
        borderColor: token.colorPrimary,
        bodyStyle: subPanelStyle(token, token.colorPrimaryBg, token.colorPrimary),
      };
    case 'docPrepSupplierAdvance':
    case 'deliveryPartial':
    case 'crmActivities':
    case 'quotationPrAwaitingSo':
    case 'scmApprovalBid':
    case 'qmsGateStage':
    case 'qmsApprovalIrn':
      return {
        borderColor: token.colorWarning,
        bodyStyle: subPanelStyle(token, token.colorWarningBg, token.colorWarning),
      };
    case 'docPrepSalesInvoice':
    case 'deliveryInTransit':
    case 'crmQuotations':
    case 'quotationConversionAll':
    case 'scmApprovalPo':
    case 'qmsGateFir':
    case 'qmsApprovalFirReport':
      return {
        borderColor: token.colorSuccess,
        bodyStyle: subPanelStyle(token, token.colorSuccessBg, token.colorSuccess),
      };
    case 'docPrepSupplierBill':
    case 'deliveryDocPackage':
    case 'crmMissingPo':
    case 'scmApprovalGrn':
    case 'scmApprovalReturn':
    case 'qmsApprovalPlan':
      return {
        borderColor: token.colorTextSecondary,
        bodyStyle: subPanelStyle(token, token.colorFillAlter, token.colorTextSecondary),
      };
    case 'dueToday':
    case 'dueSoon':
    case 'ordersAtRisk':
      return {
        borderColor: token.colorWarning,
        cardStyle: { ...baseCard, borderLeftColor: token.colorWarning },
        headerStyle: { background: token.colorWarningBg, borderBottom: `1px solid ${token.colorBorderSecondary}` },
        bodyStyle: { background: token.colorWarningBg },
      };
    case 'pendingApprovals':
    case 'pendingReview':
    case 'sourcingQueue':
    case 'qmsPlanItemsQueue':
      return {
        borderColor: token.colorPrimary,
        cardStyle: { ...baseCard, borderLeftColor: token.colorPrimary },
        headerStyle: { background: token.colorPrimaryBg, borderBottom: `1px solid ${token.colorBorderSecondary}` },
        bodyStyle: { background: token.colorBgContainer },
      };
    case 'overdue':
    case 'collectionsDetail':
    case 'qmsNcrQueue':
    case 'qmsCapaOverdue':
      return {
        borderColor: token.colorError,
        cardStyle: { ...baseCard, borderLeftColor: token.colorError },
        headerStyle: { background: token.colorErrorBg, borderBottom: `1px solid ${token.colorBorderSecondary}` },
        bodyStyle: { background: token.colorErrorBg },
      };
    case 'monthEnd':
    case 'ordersInProgress':
    case 'pendingWorkOrder':
      return {
        borderColor: token.colorSuccess,
        cardStyle: { ...baseCard, borderLeftColor: token.colorSuccess },
        headerStyle: { background: token.colorSuccessBg, borderBottom: `1px solid ${token.colorBorderSecondary}` },
        bodyStyle: { background: token.colorSuccessBg },
      };
    default:
      return { borderColor: token.colorBorder };
  }
}

/** @deprecated Use ControlTowerZone */
export type FinanceTowerZone = ControlTowerZone;
